import { publicApi, siteApi } from "@api";
import { ONE_DAY_IN_SECONDS } from "@constants/time";
import { WSCashbackRewardUpdatedEvent, WSCashbackRewardUpdatedEventDetail } from "@constants/ws";
import useAuthSession from "@hooks/useAuthSession";
import { usePlaySpinStatus } from "@hooks/usePlaySpinStatus";
import { AppDispatch } from "@stores";
import { checkClaimable } from "@utils/reward";
import { MegaShareState } from "@views/PromotionMegaSharePage/types";
import BigNumber from "bignumber.js";
import { PlaySpinTxStatus, WalletTxMethod } from "kz-ui-sdk";
import moment, { Moment } from "moment";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  useWSCashbackRewardUpdated,
  useWSReferralCommissionRewardUpdated,
  useWSReferralTokenSettingsChange,
} from "./useAppWS";
import useGetRewardsInfo from "./useGetRewardsInfo";
import { useMegaShareStatus } from "./useMegaShareStatus";
import useServerInfo from "./useServerInfo";
import useUserMode from "./useUserMode";

interface PlayAndSpinBonusDetails {
  bonusAmt: number | BigNumber;
  targetAmt: number | BigNumber;
  currentAmt: number | BigNumber;
  status: PlaySpinTxStatus;
  startedAt?: Moment | null;
  issuedAt?: Moment | null;
  remainingSeconds?: number;
}

const DEFAULT_PLAY_AND_SPIN_BONUS_DETAILS: PlayAndSpinBonusDetails = {
  bonusAmt: 0,
  targetAmt: 1000,
  currentAmt: 1000,
  status: PlaySpinTxStatus.Issued,
  remainingSeconds: 0,
};

/**
 * Hook to get the promotion status of Daily Bonus, Cashback, and Referral
 */
const usePromotionStatus = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { isGuest } = useUserMode();
  const { isBlacklisted } = useAuthSession({
    onBlacklistUpdate: () => {
      refetchReferralRewardStatus();
      refetchRewardData();
    },
  });
  const { data: rewardData, isLoading: isFetchingRewardData, refetch: refetchRewardData } = useGetRewardsInfo();

  const { data: referralData, isLoading: isFetchingReferralStatus } = siteApi.useGetReferralInfoQuery(
    {},
    {
      skip: isGuest,
    },
  );

  const {
    data: referralClaimData,
    isLoading: isFetchingReferralClaimData,
    refetch: refetchReferralClaimData,
  } = siteApi.useGetReferralAvailableClaimQuery(
    {},
    {
      skip: isGuest || !referralData?.referralConfig?.visibility,
    },
  );
  const {
    data: referralRewardStatus,
    isLoading: isFetchingReferralRewardStatus,
    refetch: refetchReferralRewardStatus,
  } = siteApi.useGetReferralRewardStatusQuery(undefined, {
    skip: isGuest,
    refetchOnMountOrArgChange: true,
  });

  const { data: referralInfo, isFetching: isFetchingReferralInfo } = publicApi.useGetPublicReferralInfoQuery(
    undefined,
    {
      skip: !isGuest,
    },
  );

  const isGuestReferralRewardAvailable = useMemo(() => {
    if (isFetchingReferralInfo) return false;
    // @ts-ignore
    return !!referralInfo?.referralBonusTokenSettings?.enabled;
  }, [referralInfo, isFetchingReferralInfo]);

  //Countdown logic for Daily bonus
  const countingDownRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const [remainingSeconds, setRemainingSeconds] = useState(ONE_DAY_IN_SECONDS);
  const [playAndSpinRemainingSeconds, setPlayAndSpinRemainingSeconds] = useState(0);
  const playAndSpinCountingDownRef = useRef<NodeJS.Timeout | undefined>(undefined);

  const serverInfo = useServerInfo();

  const stopCountdown = useCallback(() => {
    clearInterval(countingDownRef.current);
  }, []);

  const onSecondTick = useCallback(() => {
    setRemainingSeconds((prev) => {
      if (prev === 1) {
        stopCountdown();
      }
      return prev - 1;
    });
  }, [stopCountdown]);

  const startCountdown = useCallback(() => {
    const now = new Date();
    const delay = 1000 - now.getMilliseconds();
    setTimeout(() => {
      stopCountdown();
      countingDownRef.current = setInterval(onSecondTick, 1000);
    }, delay);
  }, [onSecondTick, stopCountdown]);

  useEffect(() => {
    if (rewardData?.[WalletTxMethod.DailyBonus] && serverInfo?.utcOffset) {
      const remainingSecs = rewardData[WalletTxMethod.DailyBonus].remainingSeconds ?? 0;
      setRemainingSeconds(remainingSecs);
      if (remainingSecs > 0) {
        startCountdown();
      } else {
        stopCountdown();
      }
    }
  }, [rewardData, serverInfo?.utcOffset, startCountdown, stopCountdown]);

  useEffect(() => {
    return () => {
      clearInterval(countingDownRef.current);
      clearInterval(playAndSpinCountingDownRef.current);
    };
  }, []);

  // Sync referral token settings change from WS
  useWSReferralTokenSettingsChange({
    cb: () => {
      refetchReferralRewardStatus();
      refetchRewardData();
    },
  });

  useWSCashbackRewardUpdated({
    cb: ({ detail }: WSCashbackRewardUpdatedEvent) => {
      type DetailKey = keyof WSCashbackRewardUpdatedEventDetail;

      const detailToTxMethodMap: Record<DetailKey, WalletTxMethod> = {
        instant: WalletTxMethod.InstantCashBack,
        daily: WalletTxMethod.DailyCashBack,
        weekly: WalletTxMethod.WeeklyCashBack,
        monthly: WalletTxMethod.MonthlyCashBack,
      } as const;

      dispatch(
        siteApi.util.updateQueryData("getRewardsStatus", undefined, (draft) => {
          const detailKeys = Object.keys(detailToTxMethodMap) as DetailKey[];

          for (const detailKey of detailKeys) {
            const txKey = detailToTxMethodMap[detailKey];

            draft[txKey] = {
              ...(draft[txKey] || {}),
              ...(detail[detailKey] || {}),
            } as any;
          }
        }) as any,
      );
    },
  });

  useWSReferralCommissionRewardUpdated({
    cb: () => {
      refetchReferralClaimData();
    },
  });

  // Check daily login & reward status
  const { isCashbackAvailable, isCashbackClaimable, isDailyBonusAvailable, isDailyBonusClaimable } = useMemo(() => {
    const checkClaimableResult = checkClaimable(rewardData);
    const isCashbackClaimable =
      checkClaimableResult[WalletTxMethod.DailyCashBack] ||
      checkClaimableResult[WalletTxMethod.InstantCashBack] ||
      checkClaimableResult[WalletTxMethod.WeeklyCashBack] ||
      checkClaimableResult[WalletTxMethod.MonthlyCashBack];

    const isCashbackAvailable =
      !!rewardData?.[WalletTxMethod.DailyCashBack] ||
      !!rewardData?.[WalletTxMethod.InstantCashBack] ||
      !!rewardData?.[WalletTxMethod.WeeklyCashBack] ||
      !!rewardData?.[WalletTxMethod.MonthlyCashBack];

    return {
      isCashbackClaimable,
      isCashbackAvailable,
      isDailyBonusAvailable: !!rewardData?.[WalletTxMethod.DailyBonus],
      isDailyBonusClaimable: checkClaimableResult[WalletTxMethod.DailyBonus],
    };
  }, [rewardData]);

  // Check referral reward status
  const { isReferralRewardAvailable, isReferralRewardClaimable } = useMemo(() => {
    return {
      isReferralRewardAvailable: !!referralRewardStatus?.enabled && !!referralRewardStatus?.canParticipate,
      isReferralRewardClaimable: !!referralRewardStatus?.enabled && !!referralRewardStatus?.availableTokenCount,
    };
  }, [referralRewardStatus]);

  // Check referral status
  const { isReferralAvailable, isReferralClaimable } = useMemo(() => {
    return {
      isReferralAvailable: !!referralData?.referralConfig?.visibility,
      isReferralClaimable:
        !!referralData?.referralConfig &&
        referralClaimData?.availableAmount?.gt(0) &&
        referralClaimData?.availableAmount?.gte(referralData?.referralConfig?.minComAmt ?? 0),
    };
  }, [referralData, referralClaimData]);

  // Countdown logic for Play & Spin
  const stopPlayAndSpinCountdown = useCallback(() => {
    clearInterval(playAndSpinCountingDownRef.current);
  }, []);

  const onPlayAndSpinSecondTick = useCallback(() => {
    setPlayAndSpinRemainingSeconds((prev) => {
      if (prev <= 1) {
        stopPlayAndSpinCountdown();
        return 0;
      }
      return prev - 1;
    });
  }, [stopPlayAndSpinCountdown]);

  const startPlayAndSpinCountdown = useCallback(() => {
    const now = new Date();
    const delay = 1000 - now.getMilliseconds();
    setTimeout(() => {
      stopPlayAndSpinCountdown();
      playAndSpinCountingDownRef.current = setInterval(onPlayAndSpinSecondTick, 1000);
    }, delay);
  }, [onPlayAndSpinSecondTick, stopPlayAndSpinCountdown]);

  const {
    isPlayAndSpinAvailable,
    isPlayAndSpinClaimable,
    playAndSpinLockedInstructions,
    isPlayAndSpinLocked,
    playAndSpinBonusDetails,
    isFetchingRewardStatus,
  } = usePlaySpinStatus();

  const {
    giver: megaShareGiver,
    taker: megaShareTaker,
    details: megaShareBonusDetails,
    isFetching: isFetchingMegaShareStatus,
    isAvailable: isMegaShareAvailable,
    shouldShowGiverMegaShare,
    isGiverMegaShareUnseen,
  } = useMegaShareStatus();
  const isMegaShareGiverClaimable = megaShareGiver?.status === MegaShareState.CLAIMABLE

  useEffect(() => {
    if (playAndSpinBonusDetails?.startedAt) {
      const isPending = playAndSpinBonusDetails?.status === PlaySpinTxStatus.Pending;
      const diffInSeconds = moment(playAndSpinBonusDetails?.startedAt).diff(moment(), "seconds");
      const seconds = isPending && diffInSeconds > 0 ? Math.max(0, diffInSeconds) : 0;
      setPlayAndSpinRemainingSeconds(seconds);
      if (seconds > 0) {
        startPlayAndSpinCountdown();
      } else {
        stopPlayAndSpinCountdown();
      }
    } else {
      setPlayAndSpinRemainingSeconds(0);
      stopPlayAndSpinCountdown();
    }
  }, [
    playAndSpinBonusDetails?.startedAt,
    playAndSpinBonusDetails?.status,
    startPlayAndSpinCountdown,
    stopPlayAndSpinCountdown,
  ]);

  // combine status
  const { isAnyAvailable, isAnyClaimable } = useMemo(() => {
    return {
      isAnyAvailable:
        isCashbackAvailable ||
        isDailyBonusAvailable ||
        isReferralAvailable ||
        isReferralRewardAvailable ||
        isPlayAndSpinAvailable,
      isAnyClaimable:
        isCashbackClaimable ||
        isDailyBonusClaimable ||
        isReferralClaimable ||
        isReferralRewardClaimable ||
        isPlayAndSpinClaimable ||
        isMegaShareGiverClaimable ,
    };
  }, [
    isCashbackAvailable,
    isCashbackClaimable,
    isDailyBonusAvailable,
    isDailyBonusClaimable,
    isReferralAvailable,
    isReferralClaimable,
    isReferralRewardAvailable,
    isReferralRewardClaimable,
    isPlayAndSpinAvailable,
    isPlayAndSpinClaimable,
    isMegaShareGiverClaimable,
  ]);

  const isFetching =
    isFetchingRewardData ||
    isFetchingReferralStatus ||
    isFetchingReferralClaimData ||
    isFetchingReferralRewardStatus ||
    isFetchingReferralInfo ||
    isFetchingRewardStatus ||
    isFetchingMegaShareStatus;

  const shouldHideDailyBonus = useMemo(() => {
    return isBlacklisted && !isDailyBonusAvailable;
  }, [isBlacklisted, isDailyBonusAvailable]);

  const shouldHideReferralReward = useMemo(() => {
    return !referralRewardStatus?.enabled;
  }, [referralRewardStatus]);

  const shouldHidePlayAndSpin = useMemo(() => {
    return !isPlayAndSpinAvailable;
  }, [isPlayAndSpinAvailable]);

  useEffect(() => {
    if (isGuest) {
      return;
    }

    // The countdown end we will refetch the reward data to see if we can claim the daily bonus
    if (remainingSeconds <= 0 && !isDailyBonusClaimable) {
      // Prevent infinite loop. We will waiting for the next refetch to set the remaining seconds or display claimable
      setRemainingSeconds(ONE_DAY_IN_SECONDS);
      refetchRewardData();
    }
  }, [isDailyBonusClaimable, isGuest, refetchRewardData, remainingSeconds]);

  // Enable all promotions for open mode
  if (isGuest) {
    return {
      isCashbackAvailable: true,
      isCashbackClaimable: true,
      isDailyBonusAvailable: true,
      isDailyBonusClaimable: true,
      isReferralAvailable: true,
      isReferralClaimable: true,
      isReferralRewardAvailable: isGuestReferralRewardAvailable,
      isReferralRewardClaimable: true,
      isAnyAvailable: true,
      isAnyClaimable: true,
      isFetching,
      shouldHideDailyBonus: false,
      shouldHideReferralReward: !isGuestReferralRewardAvailable,
      isPlayAndSpinAvailable: serverInfo?.playSpinSettings?.enabled,
      isPlayAndSpinClaimable: true,
      playAndSpinBonusDetails: DEFAULT_PLAY_AND_SPIN_BONUS_DETAILS,
      playAndSpinRemainingSeconds: null,
      // mega share
      isMegaShareAvailable: false,
      isMegaShareGiverClaimable: false,
      isMegaShareTakerClaimable: false,
      megaShareBonusDetails,
      megaShareRemainingSeconds: 0,
      shouldHideGiverMegaShare: true,
    };
  }

  // Return the status for member mode
  return {
    // cashback
    isCashbackAvailable,
    isCashbackClaimable,
    // daily login
    isDailyBonusAvailable,
    isDailyBonusClaimable,
    remainingSeconds,
    // referral
    isReferralAvailable,
    isReferralClaimable,
    isAnyAvailable,
    isAnyClaimable,
    isFetching,
    isReferralRewardAvailable,
    isReferralRewardClaimable,
    shouldHideDailyBonus,
    shouldHideReferralReward,

    // play and spin
    isPlayAndSpinAvailable,
    isPlayAndSpinClaimable,
    playAndSpinLockedInstructions,
    isPlayAndSpinLocked,
    playAndSpinBonusDetails,
    playAndSpinRemainingSeconds,
    shouldHidePlayAndSpin,

    // mega share
    isMegaShareAvailable: isMegaShareAvailable,
    isMegaShareGiverClaimable,
    isMegaShareTakerClaimable: megaShareTaker?.status === MegaShareState.CLAIMABLE,
    megaShareBonusDetails,
    megaShareRemainingSeconds: megaShareBonusDetails?.remainingSeconds ?? 0,
    shouldHideGiverMegaShare: !shouldShowGiverMegaShare,
    isGiverMegaShareUnseen
  };
};

export default usePromotionStatus;
